import React, { useState } from 'react';

interface ExerciseCardProps {
  question: string;
  options: string[];
  exerciseIndex: number;
  onSubmit: (answer: string) => void;
  showResult?: boolean;
  correctAnswer?: string;
  userAnswer?: string;
  fullExercise?: any; // Contains the full MCQ data with explanations
}

const ExerciseCard: React.FC<ExerciseCardProps> = ({ 
  question, 
  options, 
  exerciseIndex, 
  onSubmit, 
  showResult, 
  correctAnswer, 
  userAnswer,
  fullExercise
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleSubmit = () => {
    if (selectedOption) {
      onSubmit(selectedOption);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">Question {exerciseIndex + 1}</span>
          {fullExercise && fullExercise.metadata && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {fullExercise.metadata.ability}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {fullExercise.metadata.level}
              </span>
            </div>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mt-1">{question}</h3>
      </div>
      
      <div className="space-y-3 mb-6">
        {options.map((option, index) => {
          const isSelected = selectedOption === option || (showResult && userAnswer === option);
          const isCorrect = showResult && option === correctAnswer;
          const isIncorrect = showResult && userAnswer === option && option !== correctAnswer;
          
          return (
            <label 
              key={index}
              onClick={(e) => {
                e.preventDefault();
                if (!showResult) {
                  setSelectedOption(option);
                }
              }}
              className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                showResult
                  ? isCorrect
                    ? 'border-green-500 bg-green-50'
                    : isIncorrect
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 bg-white'
                  : isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={`question-${exerciseIndex}`}
                value={option}
                checked={!!isSelected}
                onChange={() => {}} // Handled by label onClick
                disabled={showResult}
                className="sr-only"
                onFocus={(e) => e.preventDefault()}
              />
              <div className="flex items-center">
                <span className={`text-base ${
                  showResult
                    ? isCorrect
                      ? 'text-green-700 font-semibold'
                      : isIncorrect
                      ? 'text-red-700'
                      : 'text-gray-600'
                    : isSelected
                    ? 'text-blue-700'
                    : 'text-gray-700'
                }`}>
                  {option}
                </span>
                {showResult && isCorrect && (
                  <svg className="w-5 h-5 ml-auto text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {showResult && isIncorrect && (
                  <svg className="w-5 h-5 ml-auto text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
            </label>
          );
        })}
      </div>
      
      {!showResult && (
        <button
          onClick={handleSubmit}
          disabled={!selectedOption}
          style={{
            width: '100%',
            paddingLeft: '24px',
            paddingRight: '24px',
            paddingTop: '13px',
            paddingBottom: '13px',
            background: selectedOption ? '#042434' : '#E5E7EB',
            borderRadius: '100px',
            color: selectedOption ? 'white' : '#9CA3AF',
            fontSize: '16px',
            fontFamily: 'Lato',
            fontWeight: '700',
            wordWrap: 'break-word',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px',
            flexWrap: 'wrap',
            alignContent: 'center',
            cursor: selectedOption ? 'pointer' : 'not-allowed'
          }}
          onMouseEnter={(e) => {
            if (selectedOption) {
              e.currentTarget.style.background = '#063247';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedOption) {
              e.currentTarget.style.background = '#042434';
            }
          }}
          className="transition-opacity"
        >
          Submit Answer
        </button>
      )}
      
      {showResult && (
        <div className={`mt-4 p-4 rounded-lg ${
          userAnswer === correctAnswer ? 'bg-green-100' : 'bg-red-100'
        }`}>
          <p className={`text-sm font-medium mb-2 ${
            userAnswer === correctAnswer ? 'text-green-800' : 'text-red-800'
          }`}>
            {userAnswer === correctAnswer ? 'Correct!' : `Incorrect. The correct answer is: ${correctAnswer}`}
          </p>
          {fullExercise && fullExercise.content && fullExercise.content.options && (
            <p className="text-sm text-gray-600">
              {fullExercise.content.options.find((opt: any) => 
                opt.answer_text === userAnswer
              )?.explanation || ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ExerciseCard;